import React, {
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Badge,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
import AdminHeader from "components/Headers/AdminHeader";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { formatText } from "components/CommonComponents/CommonFunctions";
import {
  getCourseById,
  getChannelPartnerEnrolledCourseScheduleDetails,
  getCourseReviews,
  channelPartnerTraineeCourseAttendance,
} from "action/course";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";
import { urlDecodedId } from "components/CommonComponents/encodeDecodeFunction";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import { DEFAULT_RECORDS_PER_PAGE } from "components/constant";
import EnrolledTraineesTab from "pages/Courses/ChannelPartner/EnrolledTraineesTab";

// Read More/Less Component for long text (same as EnrollPage)
const ReadMoreText = ({ text, maxLength = 300, className = "" }) => {
  const [expanded, setExpanded] = useState(false);

  if (!text) return null;

  const shouldTruncate = text.length > maxLength;
  const displayText =
    shouldTruncate && !expanded ? text.slice(0, maxLength) + "..." : text;

  return (
    <div className={className}>
      <div
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(displayText),
        }}
      />
      {shouldTruncate && (
        <button
          type="button"
          className="read-more-btn mt-1 read-more-button"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
        >
          {expanded ? "Read Less" : "Read More"}
          <i
            className={`read-more-button-icon fas fa-chevron-${expanded ? "up" : "down"} ml-1`}
          />
        </button>
      )}
    </div>
  );
};

const renderStars = (rating) => (
  <span className="enrolled-stars">
    {Array.from({ length: 5 }).map((_, i) => (
      <i
        key={i}
        className={`${i < Math.round(rating) ? "fas" : "far"} fa-star mr-1`}
      />
    ))}
  </span>
);

const ChannelPartnerEnrolledCourseDetails = () => {
  const navigate = useNavigate();
  const { notify } = useContext(NotificationContext);
  const { id } = useParams();
  const location = useLocation();
  const batchId = location.state?.batchId;
  const courseId = id ? urlDecodedId(id) : null;

  const [courseDetails, setCourseDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [openLesson, setOpenLesson] = useState(null);
  const [isCertificateModalOpen, setIsCertificateModalOpen] = useState(false);
  const [modalScale, setModalScale] = useState(1);
  const modalObserverRef = useRef(null);
  const [activeTab, setActiveTab] = useState("1");
  const [openBatchIndex, setOpenBatchIndex] = useState(null);
  const scheduleTextRef = useRef(null);
  const previewObserverRef = useRef(null);
  const [previewScale, setPreviewScale] = useState(0.35);
  const videoRef = useRef(null);

  // Schedule state
  const [scheduleData, setScheduleData] = useState(null);
  const [isScheduleLoading, setIsScheduleLoading] = useState(false);
  const [scheduleFetched, setScheduleFetched] = useState(false);
  const scheduleFetchRef = useRef(false);

  // Reviews state
  const [reviewsData, setReviewsData] = useState(null);
  const [isReviewsLoading, setIsReviewsLoading] = useState(false);
  const [reviewsFetched, setReviewsFetched] = useState(false);
  const reviewsFetchRef = useRef(false);

  // Attendance state
  const [attendanceData, setAttendanceData] = useState(null);
  const [isAttendanceLoading, setIsAttendanceLoading] = useState(false);
  const [isAttendanceFetched, setIsAttendanceFetched] = useState(false);
  const attendanceFetchRef = useRef(false);
  const [attendancePagination, setAttendancePagination] = useState({
    page: 1,
    totalPages: 1,
  });

  // Fetch course details
  useEffect(() => {
    if (courseId) {
      fetchCourseDetails(courseId);
    }
  }, [courseId]);

  const fetchCourseDetails = async (courseId) => {
    try {
      setIsLoading(true);
      const res = await getCourseById(courseId);
      if (res?.code === 200) {
        setCourseDetails(res.data);
      } else {
        const message = Array.isArray(res?.message)
          ? res.message.map((err) => err.Msg || JSON.stringify(err)).join(", ")
          : res?.message || "Failed to fetch course details.";
        notify("danger", message);
      }
    } catch (error) {
      notify(
        "danger",
        error?.response?.data?.message || "Something went wrong.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch schedule data only when navigating to schedule tab
  const fetchScheduleData = useCallback(async () => {
    if (scheduleFetchRef.current) {
      return;
    }

    if (!courseId) {
      return;
    }

    try {
      setIsScheduleLoading(true);
      scheduleFetchRef.current = true;

      const res =
        await getChannelPartnerEnrolledCourseScheduleDetails(courseId);

      if (res?.code === 200) {
        setScheduleData(res.data);
        setScheduleFetched(true);
      } else {
        const message = Array.isArray(res?.message)
          ? res.message.map((err) => err.Msg || JSON.stringify(err)).join(", ")
          : res?.message || "Failed to fetch schedule details.";
        notify("danger", message);
      }
    } catch (error) {
      notify(
        "danger",
        error?.response?.data?.message || "Failed to load schedule.",
      );
    } finally {
      setIsScheduleLoading(false);
    }
  }, [courseId, notify]);

  // Fetch reviews data only when navigating to reviews tab
  const fetchReviewsData = useCallback(async () => {
    if (reviewsFetchRef.current) {
      return;
    }

    if (!courseId) {
      return;
    }

    try {
      setIsReviewsLoading(true);
      reviewsFetchRef.current = true;

      const res = await getCourseReviews(courseId);

      if (res?.code === 200) {
        setReviewsData(res.data);
        setReviewsFetched(true);
      } else {
        const message = Array.isArray(res?.message)
          ? res.message.map((err) => err.Msg || JSON.stringify(err)).join(", ")
          : res?.message || "Failed to fetch reviews.";
        notify("danger", message);
      }
    } catch (error) {
      notify(
        "danger",
        error?.response?.data?.message || "Failed to load reviews.",
      );
    } finally {
      setIsReviewsLoading(false);
    }
  }, [courseId, notify]);

  // Fetch attendance data with batchId
  const fetchAttendanceData = useCallback(async () => {
    if (attendanceFetchRef.current) {
      return;
    }

    if (!courseId) {
      return;
    }

    if (!batchId) {
      notify("warning", "No batch found for this course.");
      return;
    }

    try {
      setIsAttendanceLoading(true);
      attendanceFetchRef.current = true;

      const res = await channelPartnerTraineeCourseAttendance(
        courseId,
        batchId,
      );

      if (res?.code === 200) {
        setAttendanceData(res.data);
        setIsAttendanceFetched(true);

        // Set total pages for pagination
        const totalRecords = res.data?.totalRecords || 0;
        const recordsPerPage = res.data?.noOfRecordsPerPage || 10;
        setAttendancePagination((prev) => ({
          ...prev,
          totalPages: Math.ceil(totalRecords / recordsPerPage) || 1,
        }));
      } else {
        const message = Array.isArray(res?.message)
          ? res.message.map((err) => err.Msg || JSON.stringify(err)).join(", ")
          : res?.message || "Failed to fetch attendance.";
        notify("danger", message);
      }
    } catch (error) {
      notify(
        "danger",
        error?.response?.data?.message || "Failed to load attendance.",
      );
    } finally {
      setIsAttendanceLoading(false);
    }
  }, [courseId, notify, courseDetails]);

  // Handle tab change - fetch schedule, reviews, or attendance only when respective tab is clicked
  const handleTabChange = useCallback(
    (tab) => {
      setActiveTab(tab);

      if (tab === "3" && !scheduleFetched && !scheduleFetchRef.current) {
        fetchScheduleData();
      }

      if (tab === "2" && !reviewsFetched && !reviewsFetchRef.current) {
        fetchReviewsData();
      }

      if (tab === "5" && !isAttendanceFetched && !attendanceFetchRef.current) {
        fetchAttendanceData();
      }
    },
    [
      fetchScheduleData,
      fetchReviewsData,
      fetchAttendanceData,
      scheduleFetched,
      reviewsFetched,
      isAttendanceFetched,
    ],
  );

  const lessons = courseDetails?.lessonByLanguage?.englishLessons || [];

  // Get the enrolled batch (first batch from the list since user is enrolled)
  const enrolledBatch = courseDetails?.batches?.[0];

  // Get course type label
  const getCourseTypeLabel = (type) => {
    switch (type?.toUpperCase()) {
      case "LIVE":
        return "LIVE";
      case "RECORDED":
        return "RECORDED";
      case "OFFLINE":
        return "OFFLINE";
      default:
        return type || "COURSE";
    }
  };

  // Get status badge color
  const getStatusBadgeColor = (status) => {
    switch (status?.toUpperCase()) {
      case "ENROLLED":
        return "success";
      case "INPROGRESS":
        return "warning";
      case "COMPLETED":
        return "success";
      case "NOT_ENROLLED":
        return "secondary";
      default:
        return "secondary";
    }
  };

  // Get batch status color
  // const getBatchStatusColor = (status) => {
  //   switch (status?.toUpperCase()) {
  //     case "ACTIVE":
  //     case "INPROGRESS":
  //       return "success";
  //     case "COMPLETED":
  //       return "secondary";
  //     case "UPCOMING":
  //       return "warning";
  //     default:
  //       return "secondary";
  //   }
  // };

  // Hero stats
  const heroStats = [
    // {
    //   icon: "fa fa-clock",
    //   label: "Duration",
    //   value: courseDetails?.totalCourseDuration || "-",
    // },
    {
      icon: "fa fa-language",
      label: "Language",
      value: formatText(courseDetails?.courseLanguage) || "-",
    },
    {
      icon: "fa fa-signal",
      label: "Level",
      value: formatText(courseDetails?.level) || "-",
    },
    {
      icon: "fa fa-certificate",
      label: "Certificate",
      value: courseDetails?.enableCeritificate ? "Yes" : "No",
    },
  ];

  // Get reviews data
  const reviews = reviewsData?.TraineeRatings || [];
  const averageCourseRating = reviewsData?.averageCourseRating || 0;
  const totalReviews = reviewsData?.totalRecords || 0;

  // Calculate rating breakdown
  const ratingBreakdown = [5, 4, 3, 2, 1].map((star) => {
    const count = reviews.filter((r) => r.rating === star).length;
    const percent =
      totalReviews > 0 ? Math.round((count / totalReviews) * 100) : 0;
    return { star, count, percent };
  });

  // Attendance table columns
  const attendanceColumns = [
    {
      Header: "Sr. No.",
      accessor: "srNo",
      minWidth: 70,
      maxWidth: 80,
      centerAlign: true,
      Cell: ({ row }) => (
        <span>{(attendancePagination.page - 1) * 10 + row.index + 1}</span>
      ),
    },
    {
      Header: "Session Date",
      accessor: "sessionDate",
      minWidth: 120,
      maxWidth: 150,
      Cell: ({ value }) => <span>{value || "—"}</span>,
    },
    {
      Header: "Start Time",
      accessor: "sessionStartTime",
      minWidth: 100,
      maxWidth: 130,
      Cell: ({ value }) => <span>{value || "—"}</span>,
    },
    {
      Header: "End Time",
      accessor: "sessionEndTime",
      minWidth: 100,
      maxWidth: 130,
      Cell: ({ value }) => <span>{value || "—"}</span>,
    },
    {
      Header: "Session Type",
      accessor: "sessionType",
      minWidth: 100,
      maxWidth: 130,
      Cell: ({ value }) => (
        <Badge color={value === "ONLINE" ? "info" : "secondary"} pill>
          {value || "—"}
        </Badge>
      ),
    },
    {
      Header: "Session Status",
      accessor: "sessionStatus",
      minWidth: 120,
      maxWidth: 150,
      Cell: ({ value }) => {
        const color =
          value === "COMPLETED"
            ? "success"
            : value === "IN_PROGRESS"
              ? "warning"
              : value === "UPCOMING"
                ? "info"
                : "secondary";
        return (
          <Badge color={color} pill>
            {value || "—"}
          </Badge>
        );
      },
    },
    {
      Header: "Attendance Status",
      accessor: "attendanceStatus",
      minWidth: 120,
      maxWidth: 150,
      Cell: ({ value }) => {
        const color =
          value === "PRESENT"
            ? "success"
            : value === "ABSENT"
              ? "danger"
              : value === "LATE"
                ? "warning"
                : "secondary";
        return (
          <Badge color={color} pill>
            {value || "—"}
          </Badge>
        );
      },
    },
    {
      Header: "Marked By",
      accessor: "markedBy",
      minWidth: 130,
      maxWidth: 180,
      Cell: ({ value }) => <span>{value || "—"}</span>,
    },
    {
      Header: "Attendance Date",
      accessor: "attendanceDate",
      minWidth: 150,
      maxWidth: 200,
      Cell: ({ value }) => <span>{value || "—"}</span>,
    },
  ];

  const toggleTab = (tab) => {
    if (activeTab !== tab) handleTabChange(tab);
  };

  const toggleCertificateModal = () =>
    setIsCertificateModalOpen((prev) => !prev);

  const setModalRef = useCallback((node) => {
    if (modalObserverRef.current) {
      modalObserverRef.current.disconnect();
    }
    if (node) {
      const observer = new ResizeObserver((entries) => {
        for (let entry of entries) {
          if (entry.contentRect.width) {
            setModalScale(entry.contentRect.width / 900);
          }
        }
      });
      observer.observe(node);
      modalObserverRef.current = observer;
    }
  }, []);

  const setPreviewRef = useCallback((node) => {
    if (previewObserverRef.current) {
      previewObserverRef.current.disconnect();
    }
    if (node) {
      const observer = new ResizeObserver((entries) => {
        for (let entry of entries) {
          if (entry.contentRect.width) {
            setPreviewScale(entry.contentRect.width / 900);
          }
        }
      });
      observer.observe(node);
      previewObserverRef.current = observer;
    }
  }, []);


  // Reset fetch flags when courseId changes
  useEffect(() => {
    scheduleFetchRef.current = false;
    setScheduleFetched(false);
    setScheduleData(null);
    reviewsFetchRef.current = false;
    setReviewsFetched(false);
    setReviewsData(null);
    attendanceFetchRef.current = false;
    setIsAttendanceFetched(false);
    setAttendanceData(null);
  }, [courseId]);

  const getStatusLabel = (status) => {
    switch (status?.toUpperCase()) {
      case "INPROGRESS":
        return "Ongoing";
      case "COMPLETED":
        return "Completed";
      case "UPCOMING":
        return "Upcoming";
      default:
        return status || "Ongoing";
    }
  };

  const getStatusClass = (status) => {
    switch (status?.toUpperCase()) {
      case "ACTIVE":
        return "course-status-completed";
      case "INPROGRESS":
        return "course-status-ongoing";
      case "COMPLETED":
        return "course-status-completed";
      case "UPCOMING":
        return "course-status-upcoming";
      default:
        return "course-status-ongoing";
    }
  };

  const renderBatchItem = (item, index, isSingleBatch = false) => {
    const { batch, trainingCenter, scheduleInfo } = item;
    const isOpen = isSingleBatch || openBatchIndex === index;
    
    return (
      <div className="lesson-card" key={index}>
        <div
          className="lesson-card__header"
          onClick={() => !isSingleBatch && setOpenBatchIndex(isOpen ? null : index)}
          style={isSingleBatch ? { cursor: "default" } : {}}
        >
          <div className="lesson-card__left">
            {!isSingleBatch && (
              <div className="lesson-card__number">{index + 1}</div>
            )}
            <div className="lesson-card__info">
              <div className="lesson-card__title">
                {batch?.name || "N/A"}
              </div>
            </div>
          </div>
          <div className="lesson-card__right">
            <div className="text-sm text-muted mr-3">
              <i className="far fa-calendar-alt mr-1"></i> Batch Date: {batch?.batchStartDate || "N/A"} to {batch?.batchEndDate || "N/A"}
            </div>
            {!isSingleBatch && (
              <div className="lesson-arrow">
                <i className={`fas fa-chevron-${isOpen ? "up" : "down"}`} />
              </div>
            )}
          </div>
        </div>
        
        <div className={`lesson-card__content ${isOpen ? "show" : ""}`}>
          <Row>
            <Col md="6">
              {(batch?.registrationStartDate || batch?.registrationEndDate) && (
                <p className="mb-2 text-sm">
                  <strong><i className="fas fa-calendar-check mr-2 text-muted"></i> Registration:</strong> {batch?.registrationStartDate || "N/A"} to {batch?.registrationEndDate || "N/A"}
                </p>
              )}
              <p className="mb-2 text-sm">
                <strong><i className="far fa-clock mr-2 text-muted"></i> Batch Time:</strong> {batch?.startTime || "N/A"} - {batch?.endTime || "N/A"}
              </p>
              {batch?.trainers && batch.trainers.length > 0 && (
                <div className="mb-2 text-sm">
                  <i className="fas fa-chalkboard-teacher mr-2 text-muted"></i> 
                  {batch.trainers.length > 1 ? "Trainers:" : "Trainer:"}{" "}
                  {batch.trainers.length === 1 ? (
                    <span>{`${batch.trainers[0].user?.firstName || ''} ${batch.trainers[0].user?.lastName || ''}`}</span>
                  ) : (
                    <ul className="pl-4 mb-0 mt-1">
                      {batch.trainers.map((t, idx) => (
                        <li key={idx}>
                          {`${t.user?.firstName || ''} ${t.user?.lastName || ''}`}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </Col>
            {trainingCenter && (
              <Col md="6">
                {trainingCenter && (
                  <>
                    <p className="mb-2 text-sm">
                      <strong><i className="fas fa-building mr-2 text-muted"></i> Training Center:</strong> {trainingCenter.name}
                    </p>
                    <p className="mb-2 text-sm">
                      <strong><i className="fas fa-map-marker-alt mr-2 text-muted"></i> Training Center Address:</strong> {trainingCenter.address}
                    </p>
                  </>
                )}
              </Col>
            )}
            {scheduleInfo && (
              <Col md="12" className="mt-3">
                <div className="mb-2 text-sm">
                  <p className="mb-1 text-sm">
                    <strong><i className="fas fa-info-circle mr-2 text-muted"></i> Schedule Description:</strong>
                  </p>
                  <div
                    className="text-muted schedule-info-content"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(scheduleInfo) }}
                  />
                </div>
              </Col>
            )}
          </Row>
        </div>
      </div>
    );
  };

  // Get schedule content to display
  const renderScheduleContent = () => {
    if (isScheduleLoading) {
      return (
        <div className="text-center py-5">
          <i className="fa fa-spinner fa-spin fa-2xl text-muted" />
          <p className="mt-3 text-muted">Loading schedule...</p>
        </div>
      );
    }

    const { online = [], offline = [], both = [], recorded = [] } = scheduleData || {};
    const allBatches = [...online, ...offline, ...both, ...recorded];

    if (allBatches.length === 0) {
      return (
        <div className="loaderContainer empty-style">
          <div className="empty-icon">
            <i className="fa-solid fa-circle-info"></i>
          </div>
          <span>No schedule information available.</span>
        </div>
      );
    }

    return (
      <div className="schedule-batches-container mt-3">
        {allBatches.map((item, index) => renderBatchItem(item, index, allBatches.length === 1))}
      </div>
    );
  };

  // Get course description with fallback
  const getCourseDescription = () => {
    return (
      courseDetails?.courseDescription || "<p>No description available.</p>"
    );
  };

  // Get reviews content
  const renderReviewsContent = () => {
    if (isReviewsLoading) {
      return (
        <div className="text-center py-5">
          <i className="fa fa-spinner fa-spin fa-2xl text-muted" />
          <p className="mt-3 text-muted">Loading reviews...</p>
        </div>
      );
    }

    if (reviews.length === 0) {
      return (
        <div className="loaderContainer empty-style">
          <div className="empty-icon">
            <i className="fa-solid fa-circle-info"></i>
          </div>
          <span>No reviews available for this course yet.</span>
        </div>
      );
    }

    return (
      <Row>
        <Col lg="4">
          <Card className="content-card">
            <CardBody className="text-center">
              <div className="display-4 font-weight-bold text-dark mb-1">
                {averageCourseRating.toFixed(1)}
              </div>
              <div className="mb-2">{renderStars(averageCourseRating)}</div>
              <div className="text-muted mb-4">
                Based on {totalReviews} review
                {totalReviews !== 1 ? "s" : ""}
              </div>
              {ratingBreakdown.map(({ star, count, percent }) => (
                <div
                  key={star}
                  className="d-flex align-items-center mb-2 text-left"
                >
                  <span className="mr-2 enrolled-stars-width">
                    {star} <i className="fas fa-star enrolled-stars" />
                  </span>
                  <div className="flex-grow-1 mx-2 rounded enrolled-count-div">
                    <div
                      className="rounded enrolled-count-stars"
                      style={{
                        width: `${percent}%`,
                      }}
                    />
                  </div>
                  <span className="text-muted enrolled-count-span">
                    {count}
                  </span>
                </div>
              ))}
            </CardBody>
          </Card>
        </Col>

        <Col lg="8">
          <div className="reviews-scroll-container">
            {reviews.map((review, index) => (
              <Card className="content-card review-card" key={index}>
                <CardBody>
                  <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap">
                    <div className="d-flex align-items-center">
                      <div className="review-avatar mr-2">
                        {review.profileImagePath ? (
                          <img
                            src={review.profileImagePath}
                            alt={review.userFullName || "User"}
                            className="review-avatar-img"
                            onError={(e) => {
                              // If image fails to load, fallback to initials
                              e.target.style.display = "none";
                              e.target.parentElement.innerHTML =
                                review.userFullName?.charAt(0) || "U";
                            }}
                          />
                        ) : (
                          review.userFullName?.charAt(0) || "U"
                        )}
                      </div>
                      <div>
                        <div className="font-weight-bold text-dark">
                          {review.userFullName || "Anonymous"}
                        </div>
                        <div className="small">
                          {renderStars(review.rating)}
                        </div>
                      </div>
                    </div>
                    <span className="text-muted small">
                      {review.ratingDate || "-"}
                    </span>
                  </div>
                  <p className="channelPartner-Enrolled-desc-text mb-0">
                    {review.review || "No comment provided."}
                  </p>
                </CardBody>
              </Card>
            ))}
          </div>
        </Col>
      </Row>
    );
  };

  // Render attendance content
  const renderAttendanceContent = () => {
    if (isAttendanceLoading) {
      return (
        <div className="loaderContainer">
          <i className="fa fa-spinner fa-spin fa-2xl loader" />
        </div>
      );
    }

    const attendanceList = attendanceData?.attendanceDetails || [];

    if (attendanceList.length === 0) {
      return (
        <div className="loaderContainer empty-style">
          <div className="empty-icon">
            <i className="fa-solid fa-circle-info"></i>
          </div>
          <span>No attendance records found.</span>
        </div>
      );
    }

    // Format data for table
    const formattedAttendance = attendanceList.map((item, index) => ({
      ...item,
      srNo: (attendancePagination.page - 1) * 10 + index + 1,
    }));

    return (
      <div className="table-responsive">
        <CommonTable
          columns={attendanceColumns}
          data={formattedAttendance}
          activePage={attendancePagination.page}
          setPageNum={(page) =>
            setAttendancePagination((prev) => ({ ...prev, page }))
          }
          defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
          showPaginationBottom
          totalPages={attendancePagination.totalPages}
        />
      </div>
    );
  };

  // Handle video play/pause
  const handleVideoClick = () => {
    const video = videoRef.current;
    if (video) {
      if (video.paused) {
        video.play();
      } else {
        video.pause();
      }
    }
  };

  if (isLoading) {
    return (
      <>
        <AdminHeader />
        <Container fluid className="mt--6">
          <Row>
            <Col md="12">
              <div className="loaderContainer mt-5">
                <i className="fa fa-spinner fa-spin fa-2xl loader" />
              </div>
            </Col>
          </Row>
        </Container>
      </>
    );
  }

  if (!courseDetails) {
    return (
      <>
        <AdminHeader />
        <Container fluid className="mt--6">
          <Row>
            <Col md="12">
              <div className="loaderContainer text-muted d-flex flex-column">
                <h4 className="text-muted mt-4">No course details available</h4>
              </div>
            </Col>
          </Row>
        </Container>
      </>
    );
  }

  return (
    <>
      <AdminHeader name="Course Enrollment Details" />
      <Container fluid className="mt--6">
        <Row>
          <Col md="12">
            <div
              className="d-inline-flex align-items-center cursor text-white mb-3 back-btn"
              onClick={() => {
                if (window.history.length > 1) {
                  navigate(-1);
                } else {
                  navigate("/user/home");
                }
              }}
            >
              <i className="fa-solid fa-angle-left mr-2" />
              <span className="font-weight-bold">Back</span>
            </div>

            {/* hero section */}
            <Card className="enrolled-hero-card">
              <CardBody>
                <Row className="align-items-center">
                  <Col lg="7">
                    <div className="d-flex align-items-center mb-3 flex-wrap">
                      {courseDetails.courseType && (
                        <Badge color="danger" pill className="mr-2 px-3 py-2">
                          <i className="fa fa-circle mr-1 enrolled-course-type-label" />
                          {getCourseTypeLabel(courseDetails.courseType)}
                        </Badge>
                      )}
                      {courseDetails.enrollmentStatus && (
                        <Badge
                          color={getStatusBadgeColor(
                            courseDetails.enrollmentStatus,
                          )}
                          className="px-3 py-2"
                          pill
                        >
                          <i className="fa fa-check mr-1" />
                          {formatText(courseDetails.enrollmentStatus)}
                        </Badge>
                      )}
                      {/* Rating Badge */}
                      {courseDetails.averageRating > 0 && (
                        <Badge  pill className="px-3 py-2 enrolled-stars">
                          <i className="fa fa-star mr-1" />
                          {courseDetails.averageRating.toFixed(1)}
                        </Badge>
                      )}
                    </div>
                    <h1 className="enrolled-course-title">
                      {courseDetails.courseName}
                    </h1>
                    <p className="text-muted mb-4">
                      {courseDetails.courseCategoryName}
                    </p>

                    <Row>
                      {heroStats.map((stat) => (
                        <Col xs="6" md="4" key={stat.label} className="mb-3">
                          <div className="d-flex align-items-center">
                            <div className="info-icon mr-2">
                              <i className={`${stat.icon} text-deep-blue`} />
                            </div>
                            <div>
                              <div className="enrolled-card-values">
                                {stat.value}
                              </div>
                              <div className="text-muted small">
                                {stat.label}
                              </div>
                            </div>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </Col>

                  <Col lg="5" className="text-center mt-4 mt-lg-0">
                    <div className="course-media-wrapper position-relative">
                      {courseDetails.thumbnailPath?.match(
                        /\.(mp4|webm|ogg|mov|m4v)$/i,
                      ) ? (
                        <div className="video-thumbnail-wrapper position-relative">
                          <video
                            ref={videoRef}
                            className="img-fluid rounded enrolled-course-video"
                            src={courseDetails.thumbnailPath}
                            preload="metadata"
                            playsInline
                            onClick={handleVideoClick}
                          />

                          <div
                            className="video-play-overlay enrolled-course-div"
                            onClick={handleVideoClick}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor =
                                "rgba(0,0,0,0.8)";
                              e.currentTarget.style.transform =
                                "translate(-50%, -50%) scale(1.1)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor =
                                "rgba(0,0,0,0.6)";
                              e.currentTarget.style.transform =
                                "translate(-50%, -50%) scale(1)";
                            }}
                          >
                            <i className="fas fa-play enrolled-course-play-button" />
                          </div>
                        </div>
                      ) : (
                        <img
                          src={courseDetails.thumbnailPath}
                          alt={courseDetails.courseName}
                          className="img-fluid rounded enrolled-course-image"
                        />
                      )}

                      {/* Status on image/video */}
                      <span
                        className={`course-status course-status-overlay ${getStatusClass(
                          enrolledBatch.status,
                        )}`}
                      >
                        <span className="course-status-dot" />
                        {getStatusLabel(enrolledBatch.status)}
                      </span>
                    </div>
                    {/* Batch Details */}
                    {/* {enrolledBatch && (
                      <div className="enrolled-batch-details mt-2">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <span className="text-muted">Batch: </span>
                            <strong className="text-deep-blue">
                              {enrolledBatch.name}
                            </strong>
                          </div>
                        </div>
                      </div>
                    )} */}
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {/* section navigation */}
            <Nav tabs className="enrolled-nav mt-2">
              <NavItem>
                <NavLink
                  href="#"
                  className={`enrolled-nav__link ${activeTab === "1" ? "active" : ""}`}
                  onClick={(e) => {
                    e.preventDefault();
                    toggleTab("1");
                  }}
                >
                  <i className="fa fa-book mr-2" />
                  Course Details
                </NavLink>
              </NavItem>

              <NavItem>
                <NavLink
                  href="#"
                  className={`enrolled-nav__link ${activeTab === "3" ? "active" : ""}`}
                  onClick={(e) => {
                    e.preventDefault();
                    toggleTab("3");
                  }}
                >
                  <i className="fa fa-calendar mr-2" />
                  Schedule
                  {isScheduleLoading && (
                    <i className="fa fa-spinner fa-spin ml-2" />
                  )}
                </NavLink>
              </NavItem>

              <NavItem>
                <NavLink
                  href="#"
                  className={`enrolled-nav__link ${activeTab === "4" ? "active" : ""}`}
                  onClick={(e) => {
                    e.preventDefault();
                    toggleTab("4");
                  }}
                >
                  <i className="fa fa-play-circle mr-2" />
                  Lessons
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  href="#"
                  className={`enrolled-nav__link ${activeTab === "6" ? "active" : ""}`}
                  onClick={(e) => {
                    e.preventDefault();
                    toggleTab("6");
                  }}
                >
                  <i className="fa fa-users mr-2" />
                  Enrolled Trainees
                  {/* {isAttendanceLoading && (
                    <i className="fa fa-spinner fa-spin ml-2" />
                  )} */}
                </NavLink>
              </NavItem>

              {/* <NavItem>
                <NavLink
                  href="#"
                  className={`enrolled-nav__link ${activeTab === "5" ? "active" : ""}`}
                  onClick={(e) => {
                    e.preventDefault();
                    toggleTab("5");
                  }}
                >
                  <i className="fa fa-calendar-check mr-2" />
                  Trainee Attendance
                  {isAttendanceLoading && (
                    <i className="fa fa-spinner fa-spin ml-2" />
                  )}
                </NavLink>
              </NavItem> */}

              <NavItem>
                <NavLink
                  href="#"
                  className={`enrolled-nav__link ${activeTab === "2" ? "active" : ""}`}
                  onClick={(e) => {
                    e.preventDefault();
                    toggleTab("2");
                  }}
                >
                  <i className="fa fa-star mr-2" />
                  Reviews
                  {totalReviews > 0 && (
                    <Badge pill color="light" className="ml-2">
                      {totalReviews}
                    </Badge>
                  )}
                  {isReviewsLoading && (
                    <i className="fa fa-spinner fa-spin ml-2" />
                  )}
                </NavLink>
              </NavItem>
            </Nav>

            <TabContent activeTab={activeTab} className="mt-4">
              {/* ---------------- Course Details ---------------- */}
              <TabPane tabId="1">
                <Row>
                  <Col lg="8">
                    <Card className="content-card">
                      <CardBody>
                        <h3 className="section-title">
                          {courseDetails.courseName}
                        </h3>
                        {/* Updated: Using ReadMoreText component */}
                        <ReadMoreText
                          text={getCourseDescription()}
                          maxLength={300}
                          className="channelPartner-Enrolled-desc-text text-justify"
                        />
                      </CardBody>
                    </Card>

                    {courseDetails.prerequisitesDocument?.length > 0 && (
                      <Card className="content-card">
                        <CardBody>
                          <h3 className="section-title">
                            Training Prerequisites Documents
                          </h3>

                          <ul className="list-dot channelPartner-Enrolled-desc-text">
                            {courseDetails.prerequisitesDocument.map((doc) => (
                              <li key={doc.ID}>{doc.courseDocumentName}</li>
                            ))}
                          </ul>
                        </CardBody>
                      </Card>
                    )}

                    {/* <Card className="content-card">
                      <CardBody>
                        <h3 className="section-title">Certificate</h3>
                        <CardBody>
                          <div className="certificate-side-preview">
                            {courseDetails?.certificateTemplates
                              ?.certificateTemplate ? (
                              <div className="channelPartner-Enrolled-desc-text text-justify">
                                You will receive your certificate once the
                                course is completed.
                              </div>
                            ) : (
                              <div className="text-center">
                                <p className="text-muted mb-0">
                                  Certificate is not enabled for this course.
                                </p>
                              </div>
                            )}
                          </div>
                        </CardBody>
                      </CardBody>
                    </Card> */}
                  </Col>

                  <Col lg="4">
                    <Card className="content-card">
                      <CardBody>
                        <h3 className="section-title">Quick Info</h3>
                        <div className="quick-info">
                          <div className="quick-info__item">
                            <i className="fa fa-file-alt quick-info__icon" />
                            <div>
                              <div className="quick-info__label">Lessons</div>
                              <div className="quick-info__value">
                                {lessons.length}
                              </div>
                            </div>
                          </div>
                          <div className="quick-info__item">
                            <i className="fa fa-tag quick-info__icon" />
                            <div>
                              <div className="quick-info__label">
                                Access Type
                              </div>
                              <div className="quick-info__value">
                                {formatText(courseDetails.accessType)}
                              </div>
                            </div>
                          </div>
                          <div className="quick-info__item">
                            <i className="fa fa-video quick-info__icon" />
                            <div>
                              <div className="quick-info__label">
                                Course Type
                              </div>
                              <div className="quick-info__value">
                                {formatText(courseDetails.courseType)}
                              </div>
                            </div>
                          </div>
                          <div className="quick-info__item">
                            <i className="fa fa-users quick-info__icon" />
                            <div>
                              <div className="quick-info__label">
                                Total Batches
                              </div>
                              <div className="quick-info__value">
                                {courseDetails.totalBatchesCount || 0}
                              </div>
                            </div>
                          </div>
                          {courseDetails.assessmentType && (
                            <div className="quick-info__item">
                              <i className="fa fa-clipboard-list quick-info__icon" />
                              <div>
                                <div className="quick-info__label">
                                  Assessment Type
                                </div>
                                <div className="quick-info__value">
                                  {formatText(courseDetails.assessmentType)}
                                </div>
                              </div>
                            </div>
                          )}
                          {courseDetails.coursePrice > 0 && (
                            <div className="quick-info__item">
                              <i className="fa fa-money-bill-wave quick-info__icon" />
                              <div>
                                <div className="quick-info__label">Price</div>
                                <div className="quick-info__value">
                                  ₹{courseDetails.coursePrice}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </TabPane>

              {/* ---------------- Schedule ---------------- */}
              <TabPane tabId="3">
                <Card className="content-card">
                  <CardBody>
                    <h3 className="section-title">Course Schedules</h3>
                    {renderScheduleContent()}
                  </CardBody>
                </Card>
              </TabPane>

              {/* ---------------- Lessons ---------------- */}
              <TabPane tabId="4">
                <Card className="content-card">
                  <CardBody>
                    <h3 className="section-title">Course Lessons</h3>
                    <div className="lessons-list">
                      {lessons.length > 0 ? (
                        lessons.map((lesson) => {
                          const isOpen = openLesson === lesson.id;

                          return (
                            <div className="lesson-card" key={lesson.id}>
                              <div
                                className="lesson-card__header"
                                onClick={() =>
                                  setOpenLesson(isOpen ? null : lesson.id)
                                }
                              >
                                <div className="lesson-card__left">
                                  <div className="lesson-card__number">
                                    {lesson.lesson_seq_no}
                                  </div>
                                  <div className="lesson-card__info">
                                    <div className="lesson-card__title">
                                      {lesson.lesson_name}
                                    </div>
                                  </div>
                                </div>
                                <div className="lesson-card__right">
                                  {lesson.documents &&
                                    lesson.documents.length > 0 && (
                                      <span
                                        className="lesson-card__attachment"
                                        title={`${lesson.lesson_document_type || "Resource"} attached`}
                                      >
                                        <i className="fas fa-paperclip" />
                                      </span>
                                    )}
                                  <div className="lesson-arrow">
                                    <i
                                      className={`fas fa-chevron-${isOpen ? "up" : "down"}`}
                                    />
                                  </div>
                                </div>
                              </div>
                              <div
                                className={`lesson-card__content ${isOpen ? "show" : ""}`}
                              >
                                {/* Updated: Using ReadMoreText for lesson description */}
                                <ReadMoreText
                                  text={
                                    lesson?.description ||
                                    "<p>No description available.</p>"
                                  }
                                  maxLength={300}
                                  className="channelPartner-Enrolled-desc-text"
                                />

                                {lesson.documents &&
                                  lesson.documents.length > 0 && (
                                    <div className="lesson-resource">
                                      <div className="lesson-resource__icon">
                                        <i
                                          className={`fas fa-${lesson.lesson_document_type === "VIDEO" ? "video" : "file-pdf"}`}
                                        />
                                      </div>
                                      <div className="lesson-resource__info">
                                        <div className="lesson-resource__name">
                                          {lesson.lesson_document_type ||
                                            "Lesson Resource"}
                                        </div>
                                        <div className="lesson-resource__meta">
                                          {lesson.lesson_document_type ===
                                          "VIDEO"
                                            ? "Video"
                                            : "PDF"}{" "}
                                          Document
                                        </div>
                                      </div>
                                      <div className="lesson-resource__actions">
                                        <a
                                          href={lesson.documents[0]?.file_path}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="lesson-resource__btn"
                                          title={
                                            lesson.lesson_document_type ===
                                            "VIDEO"
                                              ? "Play Video"
                                              : "View PDF"
                                          }
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <i
                                            className={`fas fa-${lesson.lesson_document_type === "VIDEO" ? "play" : "eye"}`}
                                          />
                                        </a>
                                        <a
                                          href={lesson.documents[0]?.file_path}
                                          download
                                          className="lesson-resource__btn"
                                          title="Download"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <i className="fas fa-download" />
                                        </a>
                                      </div>
                                    </div>
                                  )}
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-muted">No lessons available.</p>
                      )}
                    </div>
                  </CardBody>
                </Card>
              </TabPane>

              {/* ---------------- Attendance ---------------- */}
              <TabPane tabId="5">
                <Card className="content-card">
                  <CardBody>
                    <h3 className="section-title">Attendance Records</h3>
                    {!isAttendanceFetched && !isAttendanceLoading ? (
                      <div className="text-center py-4">
                        <p className="text-muted">
                          Click the Attendance tab to load records.
                        </p>
                      </div>
                    ) : (
                      renderAttendanceContent()
                    )}
                  </CardBody>
                </Card>
              </TabPane>

              {/* ---------------- Enrolled Trainees Tab ---------------- */}
              <TabPane tabId="6">
                <EnrolledTraineesTab courseId={courseId} />
              </TabPane>

              {/* ---------------- Reviews (Dynamic from API) ---------------- */}
              <TabPane tabId="2">{renderReviewsContent()}</TabPane>
            </TabContent>
          </Col>
        </Row>
      </Container>

      {/* Certificate Modal */}
      <Modal
        isOpen={isCertificateModalOpen}
        toggle={toggleCertificateModal}
        size="xl"
        centered
        className="certificate-preview-modal"
      >
        <ModalHeader toggle={toggleCertificateModal}>
          Certificate Preview
        </ModalHeader>

        <ModalBody className="certificate-preview-modal-body">
          {courseDetails?.enableCeritificate &&
          courseDetails?.certificateTemplates?.certificateTemplate ? (
            (() => {
              try {
                const templateJson = JSON.parse(
                  courseDetails.certificateTemplates.certificateTemplate,
                );
                return (
                  <div
                    className="certificate-preview-container mx-auto"
                    ref={setModalRef}
                  >
                    <div
                      className="course-details-certificate-preview-canvas"
                      style={{
                        transform: `scale(${modalScale})`,
                        backgroundImage: `url(${templateJson.previewImage || "/certificate-bg.png"})`,
                      }}
                    >
                      {templateJson.layout?.map((item, index) => {
                        if (item.type === "text") {
                          return (
                            <div
                              key={index}
                              style={{
                                position: "absolute",
                                left: `${item.x}%`,
                                top: `${item.y}%`,
                                fontSize: `${item.fontSize || 24}px`,
                                fontFamily: item.fontFamily || "Arial",
                                color: item.color || "#000",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {item.text}
                            </div>
                          );
                        }

                        if (
                          item.type === "signature" ||
                          item.type === "image"
                        ) {
                          return (
                            <img
                              key={index}
                              src={item.src}
                              alt="signature"
                              style={{
                                position: "absolute",
                                left: `${item.x}%`,
                                top: `${item.y}%`,
                                width: `${(item.scaleX || 0.5) * 32}%`,
                                height: "auto",
                                objectFit: "contain",
                              }}
                            />
                          );
                        }

                        if (
                          item.type === "shape" &&
                          item.shapeType === "line"
                        ) {
                          return (
                            <div
                              key={index}
                              style={{
                                position: "absolute",
                                left: `${item.x}%`,
                                top: `${item.y}%`,
                                width: `${((item.x2 - item.x1) / 900) * 100}%`,
                                height: `${item.strokeWidth || 2}px`,
                                backgroundColor: item.stroke || "#000",
                              }}
                            />
                          );
                        }

                        return null;
                      })}
                    </div>
                  </div>
                );
              } catch (err) {
                return (
                  <div className="text-center text-danger">
                    Error rendering certificate template.
                  </div>
                );
              }
            })()
          ) : (
            <div className="text-center p-5 text-muted">
              No certificate added.
            </div>
          )}
        </ModalBody>
      </Modal>
    </>
  );
};

export default ChannelPartnerEnrolledCourseDetails;
